// Bulk upload, the second way in on Invite People: the admin downloads the
// template, fills in First name, Last name, Email and Role, uploads it (CSV or
// Excel) and checks the rows before anything is sent. Each ready row is one
// org invitation (POST org/{authId}/members/invite), as inviting one person
// is; people already in the organization or invited are skipped. The rules
// are in bulkInvite.ts.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { attributeKeys, events } from '../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../utils/amplitudeProductTracking';
import { getLLAuthHeaders } from '../../../../utils/llAuthHeaders';
import { MOBILE_SX } from '../../../../mobile/mobileApp';
import {
  BULK_LIMIT,
  countRows,
  inviteEach,
  readRows,
  roleChoicesText,
  templateCsv,
  type BulkRow,
  type ReadResult,
  type RoleLabels
} from './bulkInvite';

const textSx = { fontSize: 13, color: '#B6B6B6', fontFamily: 'Inter, sans-serif', lineHeight: 1.5 };
const cellSx = {
  fontSize: 13,
  color: '#FDFDFD',
  fontFamily: 'Inter, sans-serif',
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};
const COLS = '40px 1.2fr 1.6fr 90px 1.4fr';

const STATE_COLOR: Record<BulkRow['state'], string> = {
  ready: '#00972D',
  member: '#868686',
  invited: '#868686',
  duplicate: '#868686',
  problem: '#FF6A55'
};

const peopleCount = (n: number) => (n === 1 ? '1 person' : `${n} people`);

const BulkInvitePanel: React.FC<{
  authId: string;
  labels: RoleLabels;
  /** Emails of the invitations still pending (the Members page's Invited tab). */
  invitedEmails: string[];
  onInvited: () => void;
  onClose: () => void;
  /** Sending: the dialog can't be closed meanwhile. */
  onBusyChange: (busy: boolean) => void;
}> = ({ authId, labels, invitedEmails, onInvited, onClose, onBusyChange }) => {
  const fileInput = useRef<HTMLInputElement>(null);
  // Who is already in the organization, by email: read once, and nothing is sent until it is known.
  const [memberEmails, setMemberEmails] = useState<Set<string> | null>(null);
  const [membersFailed, setMembersFailed] = useState(false);
  const [fileName, setFileName] = useState('');
  const [read, setRead] = useState<ReadResult | null>(null);
  const [reading, setReading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(0);
  const [outcome, setOutcome] = useState<Map<number, true | string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const { authorization } = getLLAuthHeaders();
    API.getUsersByOrgId(authorization, authId)
      .then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.data) ? res.data : [];
        setMemberEmails(new Set(list.map((m) => String(m?.email || '').toLowerCase()).filter(Boolean)));
      })
      .catch(() => {
        if (!cancelled) setMembersFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [authId]);

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([templateCsv()], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Dosen people upload.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const readFile = async (file: File) => {
    if (!memberEmails) return;
    setReading(true);
    setOutcome(null);
    setFileName(file.name);
    try {
      // SheetJS reads CSV and Excel alike. Loaded only here, so the Members page doesn't carry it.
      const XLSX = await import('xlsx');
      const book = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = book.Sheets[book.SheetNames[0]];
      const cells = sheet
        ? (XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }) as unknown[][])
        : [];
      setRead(
        readRows(cells, {
          labels,
          memberEmails,
          invitedEmails: new Set(invitedEmails.map((e) => e.toLowerCase()))
        })
      );
    } catch {
      setRead({
        rows: [],
        fileProblem: "This file couldn't be read. Upload a .csv or .xlsx file made from the template."
      });
    } finally {
      setReading(false);
    }
  };

  const rows = read?.rows ?? [];
  const counts = countRows(rows);
  const ready = rows.filter((r) => r.state === 'ready');
  const finished = outcome !== null;
  const invitedCount = outcome ? Array.from(outcome.values()).filter((v) => v === true).length : 0;

  const send = async () => {
    if (!ready.length || sending) return;
    setSending(true);
    onBusyChange(true);
    setSent(0);
    try {
      const result = await inviteEach(
        ready,
        async (row) => {
          await API.inviteOrgMember(authId, {
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            role: [row.role as string],
            programIds: []
          });
          trackWithBaseProps(events.userInvited, {
            inviteeEmail: row.email,
            inviteeRole: row.role,
            [attributeKeys.enrollmentType]: 'Bulk Upload'
          });
        },
        setSent
      );
      setOutcome(result);
      const ok = Array.from(result.values()).filter((v) => v === true).length;
      const failed = result.size - ok;
      if (ok) toast.success(`${peopleCount(ok)} invited.`);
      if (failed) toast.error(`${peopleCount(failed)} couldn't be invited. The list says why.`);
      if (ok) onInvited();
    } finally {
      setSending(false);
      onBusyChange(false);
    }
  };

  const statusOf = (row: BulkRow): { text: string; color: string } => {
    const sentAs = outcome?.get(row.line);
    if (sentAs === true) return { text: 'Invited', color: '#00972D' };
    if (typeof sentAs === 'string') return { text: sentAs, color: '#FF6A55' };
    return { text: row.state === 'ready' ? 'Ready' : row.note, color: STATE_COLOR[row.state] };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={textSx}>
        Fill in the template with each person&rsquo;s First name, Last name, Email and Role, then upload it as a CSV or
        Excel file. Role is {roleChoicesText(labels)}; leave it empty to invite them as {labels.mentee}. Up to{' '}
        {BULK_LIMIT} people at a time. Anyone already in the organization or already invited is skipped.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <DosenButton tone="basic" sizeVariant="sm" onClick={downloadTemplate}>
          Download template
        </DosenButton>
        <DosenButton
          tone="cta"
          styleVariant="outline"
          sizeVariant="sm"
          disabled={!memberEmails || reading || sending}
          loading={reading}
          loadingText="Reading..."
          onClick={() => fileInput.current?.click()}
        >
          {read ? 'Upload another file' : 'Upload file'}
        </DosenButton>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv"
          aria-label="Upload a CSV or Excel file"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) readFile(file);
          }}
        />
        {fileName && <Typography sx={{ ...textSx, color: '#FDFDFD' }}>{fileName}</Typography>}
      </Box>

      {membersFailed ? (
        <Typography sx={{ ...textSx, color: '#FF6A55' }}>
          Who is already in the organization couldn&rsquo;t be checked, so nothing can be uploaded. Close and try again.
        </Typography>
      ) : !memberEmails ? (
        <Typography sx={textSx}>Checking who is already in the organization...</Typography>
      ) : null}

      {read?.fileProblem && (
        <Typography role="alert" sx={{ ...textSx, color: '#FF6A55' }}>
          {read.fileProblem}
        </Typography>
      )}

      {rows.length > 0 && (
        <>
          <Typography role="status" aria-label="Upload summary" sx={{ ...textSx, color: '#FDFDFD', fontWeight: 600 }}>
            {finished
              ? `${peopleCount(invitedCount)} invited` +
                (ready.length - invitedCount ? `, ${peopleCount(ready.length - invitedCount)} not` : '') +
                '.'
              : `${peopleCount(counts.ready)} ready to invite` +
                (counts.skipped ? `, ${counts.skipped} skipped` : '') +
                (counts.problems ? `, ${counts.problems} to fix in the file` : '') +
                '.'}
          </Typography>
          <Box
            role="table"
            aria-label="People in the file"
            sx={{
              border: '1px solid #2A2A2A',
              borderRadius: '8px',
              maxHeight: 320,
              overflowY: 'auto',
              [MOBILE_SX]: { maxHeight: 'none' }
            }}
          >
            <Box
              role="row"
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                gap: '10px',
                px: '12px',
                py: '8px',
                position: 'sticky',
                top: 0,
                bgcolor: '#151515',
                borderBottom: '1px solid #2A2A2A',
                [MOBILE_SX]: { display: 'none' }
              }}
            >
              {['Line', 'Name', 'Email', 'Role', 'Status'].map((h) => (
                <Typography key={h} role="columnheader" sx={{ ...cellSx, fontSize: 11, color: '#868686' }}>
                  {h}
                </Typography>
              ))}
            </Box>
            {rows.map((row) => {
              const status = statusOf(row);
              return (
                <Box
                  key={row.line}
                  role="row"
                  aria-label={`Line ${row.line}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: COLS,
                    gap: '10px',
                    px: '12px',
                    py: '8px',
                    borderTop: '1px solid #1F1F1F',
                    [MOBILE_SX]: { gridTemplateColumns: '1fr', gap: '2px' }
                  }}
                >
                  <Typography role="cell" sx={{ ...cellSx, color: '#868686' }}>
                    {row.line}
                  </Typography>
                  <Typography role="cell" sx={cellSx}>
                    {`${row.firstName} ${row.lastName}`.trim() || '-'}
                  </Typography>
                  <Typography role="cell" sx={cellSx}>
                    {row.email || '-'}
                  </Typography>
                  <Typography role="cell" sx={cellSx}>
                    {row.role ? labels[row.role] : '-'}
                  </Typography>
                  <Typography role="cell" sx={{ ...cellSx, color: status.color, whiteSpace: 'normal' }}>
                    {status.text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        {finished ? (
          <DosenButton tone="cta" onClick={onClose}>
            Done
          </DosenButton>
        ) : (
          <DosenButton
            tone="cta"
            onClick={send}
            disabled={sending || !ready.length || !memberEmails}
            loading={sending}
            loadingText={`Inviting ${sent} of ${ready.length}...`}
          >
            {ready.length ? `Invite ${peopleCount(ready.length)}` : 'Invite'}
          </DosenButton>
        )}
      </Box>
    </Box>
  );
};

export default BulkInvitePanel;
